import React, { useState, useEffect } from "react";
import ItemListPopup from "../../../ui/ItemListPopup";
import CustomButton from "../../../ui/CommonButton";
import { ReactComponent as ArrowUp } from "../../../../assets/icons/svg/arrow-up.svg";
import { ReactComponent as ArrowDown } from "../../../../assets/icons/svg/arrow-down.svg";
import { ReactComponent as Plus } from "../../../../assets/icons/svg/plus.svg";
import { ReactComponent as PlusColor } from "../../../../assets/icons/svg/plus-color.svg";
import { ReactComponent as Check } from "../../../../assets/icons/svg/check.svg";
import { ReactComponent as Cross } from "../../../../assets/icons/svg/cross.svg";
import DeletePopup from "../../../ui/DeletePopup";
import { useTags, Tag, SelectedTag } from "../../../../context/TagContext";
import { useGroups } from "../../../../context/GroupContext";
import { Edit2, Trash2 } from "lucide-react";

interface Screen {
  id: string;
  name: string;
  group: string;
  orientation: string;
  tags: SelectedTag[];
  status: string;
  createdAt: Date;
}

interface ScreenUploadPopupProps {
  onClose: () => void;
  onUpload: (screenData: {
    name: string;
    group: string;
    orientation: string;
    tags: SelectedTag[];
  }) => void;
  showToast: (type: string, title: string, message: string) => void;
  isEditMode?: boolean;
  editScreenData?: Screen | null;
}

const ScreenUploadPopup: React.FC<ScreenUploadPopupProps> = ({
  onClose,
  onUpload,
  showToast,
  isEditMode = false,
  editScreenData = null,
}) => {
  const { availableTags } = useTags();
  const { groups, addGroup } = useGroups();

  const [screenName, setScreenName] = useState("");
  const [screenGroup, setScreenGroup] = useState("");
  const [screenOrientation, setScreenOrientation] = useState("");
  const [selectedTags, setSelectedTags] = useState<SelectedTag[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState({
    group: false,
    orientation: false,
  });
  const [orientationError, setOrientationError] = useState<string | null>(null);
  const [showAddSelectedTagInput, setShowAddSelectedTagInput] = useState(false);
  const [tempSelectedTag, setTempSelectedTag] = useState("");
  const [tempSelectedValue, setTempSelectedValue] = useState("");
  const [editingSelectedTagIndex, setEditingSelectedTagIndex] = useState<
    number | null
  >(null);
  const [editingSelectedTagData, setEditingSelectedTagData] = useState({
    tag: "",
    value: "",
  });
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<{
    index: number;
    tag: SelectedTag;
  } | null>(null);
  const [showAddGroupInput, setShowAddGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  const groupOptions = [
    { label: "None", value: "" },
    ...groups.map((group) => ({
      label: group.name,
      value: group.id,
    })),
  ];

  const orientationOptions = [
    { label: "Portrait", value: "portrait" },
    { label: "Landscape", value: "landscape" },
    { label: "Custom", value: "custom" },
  ];

  useEffect(() => {
    if (isEditMode && editScreenData) {
      setScreenName(editScreenData.name || "");
      setScreenGroup(editScreenData.group || "");
      setScreenOrientation(editScreenData.orientation || "");
      setSelectedTags(editScreenData.tags || []);
    } else {
      resetForm();
    }
  }, [isEditMode, editScreenData]);

  const resetForm = () => {
    setScreenName("");
    setScreenGroup("");
    setScreenOrientation("");
    setSelectedTags([]);
    setOrientationError(null);
    setShowAddSelectedTagInput(false);
    setTempSelectedTag("");
    setTempSelectedValue("");
    setEditingSelectedTagIndex(null);
    setEditingSelectedTagData({ tag: "", value: "" });
    setShowDeletePopup(false);
    setTagToDelete(null);
    setShowAddGroupInput(false);
    setNewGroupName("");
  };

  const handleAddSelectedTag = () => {
    if (selectedTags.length >= 5) {
      showToast(
        "error",
        "Maximum Limit",
        "You can only add up to 5 tags to a screen"
      );
      return;
    }

    if (availableTags.length === 0) {
      showToast(
        "error",
        "No Tags Available",
        "Please create tags first using the 'Add tag' button"
      );
      return;
    }

    setShowAddSelectedTagInput(true);
    setTempSelectedTag("");
    setTempSelectedValue("");
  };

  const handleSaveSelectedTag = () => {
    if (!tempSelectedTag.trim() || !tempSelectedValue.trim()) {
      showToast("error", "Invalid Input", "Both tag and value are required");
      return;
    }

    const isDuplicate = selectedTags.some(
      (selectedTag) =>
        selectedTag.tag.toLowerCase() === tempSelectedTag.trim().toLowerCase()
    );

    if (isDuplicate) {
      showToast(
        "error",
        "Duplicate Tag",
        "This tag is already added to the screen"
      );
      return;
    }

    if (selectedTags.length >= 5) {
      showToast(
        "error",
        "Maximum Limit",
        "You can only add up to 5 tags to a screen"
      );
      return;
    }

    setSelectedTags([
      ...selectedTags,
      { tag: tempSelectedTag.trim(), value: tempSelectedValue.trim() },
    ]);
    showToast(
      "success",
      "Tag Added",
      "Tag has been added to screen successfully"
    );
    setTempSelectedTag("");
    setTempSelectedValue("");
    setShowAddSelectedTagInput(false);
  };

  const handleCancelSelectedTagInput = () => {
    setTempSelectedTag("");
    setTempSelectedValue("");
    setShowAddSelectedTagInput(false);
  };

  const handleDeleteSelectedTag = (index: number) => {
    const tag = selectedTags[index];
    setTagToDelete({ index, tag });
    setShowDeletePopup(true);
  };

  const handleConfirmDelete = () => {
    if (tagToDelete) {
      setSelectedTags(selectedTags.filter((_, i) => i !== tagToDelete.index));
      showToast("success", "Tag Removed", "Tag has been removed from screen");
    }
    setShowDeletePopup(false);
    setTagToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeletePopup(false);
    setTagToDelete(null);
  };

  const handleEditSelectedTag = (index: number) => {
    const selectedTag = selectedTags[index];
    setEditingSelectedTagIndex(index);
    setEditingSelectedTagData({
      tag: selectedTag.tag,
      value: selectedTag.value,
    });
  };

  const handleSaveEditSelectedTag = () => {
    if (
      !editingSelectedTagData.tag.trim() ||
      !editingSelectedTagData.value.trim()
    ) {
      showToast("error", "Invalid Input", "Both tag and value are required");
      return;
    }

    const isDuplicate = selectedTags.some(
      (selectedTag, index) =>
        selectedTag.tag.toLowerCase() ===
          editingSelectedTagData.tag.trim().toLowerCase() &&
        index !== editingSelectedTagIndex
    );

    if (isDuplicate) {
      showToast(
        "error",
        "Duplicate Tag",
        "This tag is already added to the screen"
      );
      return;
    }

    if (editingSelectedTagIndex !== null) {
      const updatedSelectedTags = selectedTags.map((selectedTag, index) =>
        index === editingSelectedTagIndex
          ? {
              tag: editingSelectedTagData.tag.trim(),
              value: editingSelectedTagData.value.trim(),
            }
          : selectedTag
      );
      setSelectedTags(updatedSelectedTags);
      showToast("success", "Tag Updated", "Tag has been updated successfully");
      setEditingSelectedTagIndex(null);
      setEditingSelectedTagData({ tag: "", value: "" });
    }
  };

  const handleCancelEditSelectedTag = () => {
    setEditingSelectedTagIndex(null);
    setEditingSelectedTagData({ tag: "", value: "" });
  };

  const handleAddNewGroup = () => {
    setShowAddGroupInput(true);
    setNewGroupName("");
  };

  const handleSaveNewGroup = () => {
    if (!newGroupName.trim()) {
      showToast("error", "Invalid Input", "Group name is required");
      return;
    }

    const success = addGroup(newGroupName.trim());
    if (success) {
      const newGroup = groups.find((g) => g.name === newGroupName.trim());
      if (newGroup) {
        setScreenGroup(newGroup.id);
      }
      showToast("success", "Group Added", "New group has been created");
      setShowAddGroupInput(false);
      setNewGroupName("");
    } else {
      showToast("error", "Duplicate Group", "This group name already exists");
    }
  };

  const handleCancelNewGroup = () => {
    setShowAddGroupInput(false);
    setNewGroupName("");
  };

  const handleUpload = () => {
    if (!screenName.trim()) {
      showToast("error", "Required Field", "Screen name is required");
      return;
    }
    if (!screenOrientation) {
      setOrientationError("Please select screen orientation.");
      showToast("error", "Required Field", "Screen orientation is required");
      return;
    }

    onUpload({
      name: screenName.trim(),
      group: screenGroup,
      orientation: screenOrientation,
      tags: selectedTags,
    });

    resetForm();
  };

  const getUnusedTagNames = () => {
    const usedTagNames = selectedTags.map((selectedTag) =>
      selectedTag.tag.toLowerCase()
    );
    return availableTags.filter(
      (tag) => !usedTagNames.includes(tag.name.toLowerCase())
    );
  };

  const getPopupTitle = () => {
    return isEditMode ? "Edit Screen" : "Screen details";
  };

  const getButtonText = () => {
    return isEditMode ? "Update" : "Save & add";
  };

  const getSelectedGroupLabel = () => {
    if (!screenGroup) return "None";
    const selectedGroup = groups.find((g) => g.id === screenGroup);
    return selectedGroup ? selectedGroup.name : "None";
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
      <ItemListPopup title={getPopupTitle()} onClose={onClose}>
        <div className="flex flex-col h-full max-h-[90vh]">
          <div className="space-y-4 pb-4">
            <div>
              <label className="text-sm font-medium">Name *</label>
              <input
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                placeholder="Enter screen name"
                className="w-full px-3 py-2 border-[0.5px] border-[#D4D4D4] rounded-[8px] text-sm"
              />
            </div>

            <div className="relative">
              <label className="text-sm font-medium">Select screen group</label>
              <button
                onClick={() =>
                  setDropdownOpen({
                    ...dropdownOpen,
                    group: !dropdownOpen.group,
                  })
                }
                className="w-full px-3 py-2 border-[0.5px] border-[#D4D4D4] rounded-[8px] text-left text-sm flex justify-between items-center"
              >
                {getSelectedGroupLabel()}
                {dropdownOpen.group ? (
                  <ArrowUp className="w-4 h-4" />
                ) : (
                  <ArrowDown className="w-4 h-4" />
                )}
              </button>
              {dropdownOpen.group && (
                <div className="absolute left-0 right-0 border mt-2 rounded-[8px] bg-white shadow z-50 max-h-48 overflow-y-auto">
                  {groupOptions.map((opt) => (
                    <div
                      key={opt.value}
                      onClick={() => {
                        setScreenGroup(opt.value);
                        setDropdownOpen({ ...dropdownOpen, group: false });
                      }}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                    >
                      {opt.label}
                    </div>
                  ))}
                  {groups.length === 0 && !showAddGroupInput && (
                    <div className="px-4 py-2 text-gray-400 text-sm italic">
                      No groups available.
                    </div>
                  )}
                  {!showAddGroupInput && (
                    <div>
                      <hr className="border-t border-gray-300 my-1 mx-2" />
                      <div
                        onClick={handleAddNewGroup}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-[#0BA6C7]"
                      >
                        Add new group
                      </div>
                    </div>
                  )}
                  {showAddGroupInput && (
                    <div className="border-t border-gray-300 my-1 mx-2">
                      <div className="px-4 py-2 bg-gray-50 rounded-[8px]">
                        <input
                          value={newGroupName}
                          onChange={(e) => setNewGroupName(e.target.value)}
                          placeholder="Enter group name"
                          className="w-full px-3 py-2 border-[0.5px] border-[#D4D4D4] rounded-[8px] text-sm mb-2"
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={handleCancelNewGroup}>
                            <div className="p-1 2xl:p-2 border-[0.5px] border-[#D4D4D4] rounded-[8px]">
                              <Cross className="w-4 h-4 2xl:w-6 2xl:h-6 text-red-500" />
                            </div>
                          </button>
                          <button onClick={handleSaveNewGroup}>
                            <div className="p-1 2xl:p-2 border-[0.5px] border-[#D4D4D4] rounded-[8px]">
                              <Check className="w-4 h-4 2xl:w-6 2xl:h-6 text-green-500 " />
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <label className="text-sm font-medium">
                Screen orientation *
              </label>
              <button
                onClick={() =>
                  setDropdownOpen({
                    ...dropdownOpen,
                    orientation: !dropdownOpen.orientation,
                  })
                }
                className="w-full px-3 py-2 border-[0.5px] border-[#D4D4D4] rounded-[8px] text-left text-sm flex justify-between items-center"
              >
                {orientationOptions.find((o) => o.value === screenOrientation)
                  ?.label || "Select orientation"}
                {dropdownOpen.orientation ? (
                  <ArrowUp className="w-4 h-4" />
                ) : (
                  <ArrowDown className="w-4 h-4" />
                )}
              </button>
              {dropdownOpen.orientation && (
                <div className="absolute left-0 right-0 border mt-2 rounded-[8px] bg-white shadow z-50">
                  {orientationOptions.map((opt) => (
                    <div
                      key={opt.value}
                      onClick={() => {
                        setScreenOrientation(opt.value);
                        setDropdownOpen({
                          ...dropdownOpen,
                          orientation: false,
                        });
                        setOrientationError(null);
                      }}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
              {orientationError && (
                <p className="text-xs text-red-500 mt-1">{orientationError}</p>
              )}
            </div>

            {selectedTags.length > 0 && (
              <div>
                <label className="text-sm font-medium">Selected Tags</label>
                <div className="space-y-2 mt-2">
                  {selectedTags.map((selectedTag, index) => (
                    <div key={index}>
                      {editingSelectedTagIndex === index ? (
                        <div className="p-3 bg-gray-50 rounded-[8px] space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">
                              Edit Tag
                            </span>
                          </div>
                          <div className="relative">
                            <select
                              className="w-full px-3 py-2 border-[0.5px] border-[#D4D4D4] rounded-[8px] text-sm appearance-none bg-white"
                              value={editingSelectedTagData.tag}
                              onChange={(e) =>
                                setEditingSelectedTagData({
                                  ...editingSelectedTagData,
                                  tag: e.target.value,
                                })
                              }
                            >
                              <option value="">Select tag</option>
                              {availableTags.map((tag) => (
                                <option key={tag.id} value={tag.name}>
                                  {tag.name}
                                </option>
                              ))}
                              {!availableTags.some(
                                (tag) => tag.name === selectedTag.tag
                              ) && (
                                <option value={selectedTag.tag}>
                                  {selectedTag.tag}
                                </option>
                              )}
                            </select>
                            <ArrowDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" />
                          </div>
                          <input
                            className="w-full px-3 py-2 border-[0.5px] border-[#D4D4D4] rounded-[8px] text-sm"
                            placeholder="Enter value"
                            value={editingSelectedTagData.value}
                            onChange={(e) =>
                              setEditingSelectedTagData({
                                ...editingSelectedTagData,
                                value: e.target.value,
                              })
                            }
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={handleCancelEditSelectedTag}
                              className="px-4 py-1.5 border border-gray-300 text-gray-600 rounded-md text-sm hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveEditSelectedTag}
                              disabled={
                                !editingSelectedTagData.tag.trim() ||
                                !editingSelectedTagData.value.trim()
                              }
                              className={`px-4 py-1.5 text-white rounded-md text-sm ${
                                editingSelectedTagData.tag.trim() &&
                                editingSelectedTagData.value.trim()
                                  ? "bg-[#0BA6C7] hover:bg-[#0BA6C7]"
                                  : "bg-gray-300 cursor-not-allowed"
                              }`}
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-[8px]">
                          <div className="flex items-center gap-4">
                            <div className="flex flex-col min-w-[100px]">
                              <span className="text-xs text-gray-400 mb-1">
                                Tag
                              </span>
                              <span className="text-sm font-medium text-gray-800">
                                {selectedTag.tag}
                              </span>
                            </div>
                            <span className="text-black text-lg">:</span>
                            <div className="flex flex-col min-w-[100px]">
                              <span className="text-xs text-gray-400 mb-1">
                                Value
                              </span>
                              <span className="text-sm font-medium text-gray-800">
                                {selectedTag.value}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleEditSelectedTag(index)}
                              className="text-[#0BA6C7] hover:text-[#0BA6C7]/80 transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSelectedTag(index)}
                              className="text-red-500 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(selectedTags.length === 0 || showAddSelectedTagInput) && (
              <div className="border p-4 rounded-[8px] bg-[#FAFAFA]">
                <label className="text-sm font-medium">Add Tag to Screen</label>
                <div className="space-y-3 mt-2">
                  <div className="relative">
                    <select
                      className="w-full px-3 py-2 border rounded-md text-sm appearance-none bg-white"
                      value={tempSelectedTag}
                      onChange={(e) => setTempSelectedTag(e.target.value)}
                    >
                      <option value="">Select tag</option>
                      {getUnusedTagNames().map((tag) => (
                        <option key={tag.id} value={tag.name}>
                          {tag.name}
                        </option>
                      ))}
                    </select>
                    <ArrowDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" />
                  </div>
                  <input
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    placeholder="Enter value"
                    value={tempSelectedValue}
                    onChange={(e) => setTempSelectedValue(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    {showAddSelectedTagInput && selectedTags.length > 0 && (
                      <button
                        onClick={handleCancelSelectedTagInput}
                        className="px-4 py-1.5 border border-gray-300 text-gray-600 rounded-md text-sm hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={handleSaveSelectedTag}
                      disabled={!tempSelectedTag || !tempSelectedValue.trim()}
                      className={`px-4 py-1.5 text-white rounded-md text-sm ${
                        tempSelectedTag && tempSelectedValue.trim()
                          ? "bg-[#0BA6C7] hover:bg-[#0BA6C7]"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleAddSelectedTag}
              disabled={
                selectedTags.length >= 5 ||
                availableTags.length === 0 ||
                selectedTags.length === 0
              }
              className={`flex items-center gap-2 text-sm ${
                selectedTags.length >= 5 ||
                availableTags.length === 0 ||
                selectedTags.length === 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-[#0BA6C7] hover:text-[#0A99B9]"
              }`}
            >
              {selectedTags.length >= 5 ||
              availableTags.length === 0 ||
              selectedTags.length === 0 ? (
                <Plus className="w-4 h-4" />
              ) : (
                <PlusColor className="w-4 h-4" />
              )}
              Add tag
              {availableTags.length === 0 && (
                <span className="text-xs text-gray-400 ml-1">
                  (No tags available)
                </span>
              )}
            </button>
          </div>

          <div className="mt-auto pt-4 border-t flex justify-center gap-3">
            <CustomButton variant="outline" onClick={onClose}>
              Cancel
            </CustomButton>
            <CustomButton
              variant="default"
              isActive={!!screenName && !!screenOrientation}
              onClick={handleUpload}
            >
              {getButtonText()}
            </CustomButton>
          </div>
        </div>
      </ItemListPopup>

      {showDeletePopup && tagToDelete && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" />
          <DeletePopup
            title="Delete Tag"
            message={`Are you sure you want to remove the tag "${tagToDelete.tag.tag}" from this screen?`}
            onCancel={handleCancelDelete}
            onConfirm={handleConfirmDelete}
          />
        </>
      )}
    </>
  );
};

export default ScreenUploadPopup;
